<div class="channel-settings-wrapper">
  <!-- Enhanced Header Section -->
  <ng-container *ngIf="!isCreateMode">
    <div class="template-header" *ngIf="channelInfo$ | async as channelInfo">
      <div class="header-content">
        <h1 class="template-title">Channel: <span class="channel-name">{{ channelInfo.name }}</span></h1>
      </div>
    </div>
  </ng-container>
  <div class="template-header" *ngIf="isCreateMode">
    <div class="header-content">
      <h1 class="template-title">Add Channel</h1>
    </div>
  </div>

  <!-- Warning Banner -->
  <ng-container *ngIf="guild && !isCreateMode">
    <div *ngIf="channelInfo$ | async as channelInfo">
      <div class="warning-banner" *ngIf="!(guild.owner || channelInfo.has_organizer_role)">
        <div class="warning-content">
          <mat-icon class="warning-icon">warning</mat-icon>
          <div class="warning-text">
            <strong>Permission Required</strong>
            <p>Only users with the <a href="https://docs.raidorganizer.org/getting-started/terms#organizer-role" target="_blank">organizer role</a>, or higher permissions, can change these settings!</p>
          </div>
        </div>
      </div>
    </div>
  </ng-container>
  <div *ngIf="guild && isCreateMode">
    <div class="warning-banner" *ngIf="!guild.owner">
      <div class="warning-content">
        <mat-icon class="warning-icon">warning</mat-icon>
        <div class="warning-text">
          <strong>Permission Required</strong>
          <p>Only the server owner can create new channel setups from the website.</p>
        </div>
      </div>
    </div>
  </div>

  <ng-container *ngIf="guild && isCreateMode">
    <ng-container *ngIf="channelSetupInfo$ | async as setupInfo">
      <div class="settings-section">
        <div class="section-header">
          <h2 class="section-title">New Channel Setup</h2>
          <mat-icon class="section-icon">add</mat-icon>
        </div>
        <mat-form-field class="full-width-container">
          <mat-label>Channel</mat-label>
          <mat-select
            [(ngModel)]="setupTargetChannelId"
            name="setupTargetChannelId"
            (closed)="clearSetupChannelFilter()"
            [compareWith]="sameChannelId"
            [disabled]="!canManageChannelConfigs() || isChannelSetupLimitReached()">
            <mat-option>
              <ngx-mat-select-search
                [formControl]="setupChannelFilter"
                placeholderLabel="Search channels"
                noEntriesFoundLabel="No channels found">
              </ngx-mat-select-search>
            </mat-option>
            <mat-option *ngFor="let guildChannel of filteredSetupChannels" [value]="guildChannel.id">
              {{ guildChannel.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>Type</mat-label>
          <mat-select
            [(ngModel)]="setupChannelType"
            name="setupChannelType"
            [disabled]="!canManageChannelConfigs() || isChannelSetupLimitReached()">
            <mat-option value="event">event</mat-option>
            <mat-option value="lfg">lfg</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>Organizer</mat-label>
          <mat-select
            [(ngModel)]="setupOrganizerRoleId"
            name="setupOrganizerRoleId"
            [disabled]="!canManageChannelConfigs() || isChannelSetupLimitReached()">
            <mat-option *ngFor="let role of setupInfo.roles" [value]="role.id" [disabled]="role.name === '@everyone'">
              {{ role.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>User</mat-label>
          <mat-select
            [(ngModel)]="setupUserRoleId"
            name="setupUserRoleId"
            [disabled]="!canManageChannelConfigs() || isChannelSetupLimitReached()">
            <mat-option *ngFor="let role of setupInfo.roles" [value]="role.id" [disabled]="role.name === '@everyone'">
              {{ role.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <div class="action-buttons-container compact-actions">
          <div class="primary-actions">
            <button
              mat-raised-button
              color="primary"
              class="save-button"
              [disabled]="!canManageChannelConfigs() || isChannelSetupLimitReached()"
              (click)="setupChannel()">
              <mat-icon>add</mat-icon>
              <span>Add Channel</span>
            </button>
          </div>
        </div>
      </div>
    </ng-container>
  </ng-container>

  <ng-container *ngIf="guild && !isCreateMode">
    <ng-container *ngIf="channelInfo$ | async as channelInfo">
      <div class="settings-section">
        <div class="section-header">
          <h2 class="section-title">Channel Management</h2>
          <mat-icon class="section-icon">manage_accounts</mat-icon>
        </div>
        <div class="channel-management-note">
          <mat-icon>info</mat-icon>
          <span>Channel and type are immutable after setup creation.</span>
        </div>
        <div class="action-buttons-container compact-actions">
          <div class="secondary-actions">
            <button
              mat-stroked-button
              color="warn"
              class="delete-button"
              [disabled]="!canManageChannelConfigs()"
              (click)="removeCurrentChannel(channelInfo)">
              <mat-icon>delete</mat-icon>
              <span>Remove Current Channel Setup</span>
            </button>
          </div>
        </div>
      </div>
    </ng-container>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Role Settings</h2>
        <mat-icon class="section-icon">settings</mat-icon>
      </div>
      <div class="row">
      <div class="col">
        <form class="organizerRoleSelection">
          <mat-form-field class="full-width-container" floatLabel="always">
            <mat-label for="organizerRoleSelection">Organizer role</mat-label>
            <mat-select
                    [(ngModel)]="channelInfo.organizer_role"
                    name="organizerRole"
                    id="organizerRoleSelection"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                    (selectionChange)="setOrganizerRole($event)">
              <ng-container *ngFor="let role of channelInfo.roles">
                <mat-option [value]="role.id"
                        [disabled]="role.name === '@everyone'" [selected]="role.id === channelInfo.organizer_role">
                  {{ role.name }}
                </mat-option>
              </ng-container>
            </mat-select>
            <div class="mat-select-link-wrapper">
              <a href="https://docs.raidorganizer.org/getting-started/terms#organizer-role" target="_blank" class="mat-select-link">What is the Organizer Role?</a>
            </div>
          </mat-form-field>
        </form>
      </div>
      <!-- User Role selection -->
      <div class="col">
        <form class="userRoleSelection">
          <mat-form-field class="full-width-container" floatLabel="always">
            <mat-label for="userRoleSelection">User roles</mat-label>
            <mat-select
              [(ngModel)]="channelInfo.user_roles"
              name="userRoles"
              id="userRoleSelection"
              multiple
              [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
              (closed)="setUserRoles()"
              (selectionChange)="onUserRolesChanged($event.value)">

              <ng-container *ngFor="let role of channelInfo.roles">
                <mat-option
                  [value]="role.id"
                  [disabled]="
                    !channelInfo.user_roles.includes(role.id) &&
                    channelInfo.user_roles.length >= (guild.has_premium ? 25 : 1)
                  ">
                  {{ role.name }}
                </mat-option>
              </ng-container>
            </mat-select>

            <!-- Optional warning if no role selected -->
            <mat-hint *ngIf="channelInfo.user_roles.length < 1">
              At least one role must be selected.
            </mat-hint>

            <!-- Optional warning if max reached (non-blocking, just informative) -->
            <mat-hint *ngIf="channelInfo.user_roles.length >= (guild.has_premium ? 25 : 1)">
              Max {{ guild.has_premium ? 25 : 1 }} roles allowed.
            </mat-hint>

            <div class="mat-select-link-wrapper">
              <a
                href="https://docs.raidorganizer.org/getting-started/terms#user-role"
                target="_blank"
                class="mat-select-link"
              >
                What is the User Role?
              </a>
            </div>
          </mat-form-field>
        </form>
      </div>
    </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Signup Numbers</h2>
        <mat-icon class="section-icon">exposure_plus_1</mat-icon>
      </div>
    <div class="row">
      <div class="col">
        <mat-form-field class="full-width-container" floatLabel="always">
          <mat-label>Sign Up Numbers</mat-label>
          <mat-select [(ngModel)]="channelInfo.show_sign_up_numbers" name="createScheduled" [disabled]="!(guild.owner || channelInfo.has_organizer_role)" (selectionChange)="setSignupNumberDisplay($event.value)" placeholder="Hide sign up numbers">
            <!-- FIXME: This is fucking buggy! On first call, where the backend sends "false", this shit is not selected!!! -->
            <mat-option [value]="0" [selected]="'0' === channelInfo.show_sign_up_numbers">
              Hide sign up numbers
            </mat-option>
            <mat-option [value]="1" [selected]="'1' === channelInfo.show_sign_up_numbers">
              Show sign up numbers on "role" emojis
            </mat-option>
            <mat-option [value]="2" [selected]="'2' === channelInfo.show_sign_up_numbers">
              Show sign up numbers on "status" emojis
            </mat-option>
            <mat-option [value]="3" [selected]="'3' === channelInfo.show_sign_up_numbers">
              Show sign up numbers on "role" and "status" emojis
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>
    <mat-label for="signupBehavior">Signup number behavior</mat-label>
    <div class="row pe-3">
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>Reset behavior</mat-label>
          <mat-select id="resetBehavior1" [(ngModel)]="resetBehavior" name="resetBehavior" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="0">Do not reset the sign up number on emoji change</mat-option>
            <mat-option value="1">Reset sign up number on "role" to "status" emoji change</mat-option>
            <mat-option value="2">Reset sign up number on every emoji change</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>Count behavior</mat-label>
          <mat-select id="countBehavior" [(ngModel)]="countBehavior" name="countBehavior" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="1">Count every sign up of a user. (User will have 2 numbers if signed up twice)</mat-option>
            <mat-option value="2">Only count a user once. (User will have only 1 number even if signed up twice)</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-2 pe-0 d-flex align-items-end">
        <button mat-button class="confirm-button" mat-raised-button color="primary" [disabled]="!(resetBehavior && countBehavior) || (resetBehavior === resetBehaviorCurrent && countBehavior === countBehaviorCurrent)" (click)="setSignupNumberBehavior()">Confirm</button>
      </div>
    </div>
    <div class="toggle-grid">
      <div class="toggle-item toggleUserChoices">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.user_choices"
                          (change)="toggleUserChoices()">
          Show users preferred choice order next to their name
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle show_user_choices</small>
        </mat-slide-toggle>
      </div>
    </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Closed Event Settings</h2>
        <mat-icon class="section-icon">lock</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/channel_settings/event_closure" target="_blank" rel="noopener">Docs</a>
      </div>
    <div class="toggle-grid">
      <div class="toggle-item toggleEventlClosure">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.close_events"
                          (change)="toggleEventlClosure()">
          Automatically close events after the start time is reached
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/event_close toggle</small>
        </mat-slide-toggle>
      </div>
    </div>

    <form [formGroup]="eventCloseColorForm">
      <mat-form-field class="full-width-container">
        <mat-label>Color of closed events</mat-label>
        <input matInput formControlName="closeColorInputCtrl">
        <ngx-colors
          class="me-5"
          matSuffix
          ngx-colors-trigger
          formControlName="pickerCtrl"
          colorPickerControls="no-alpha"
        ></ngx-colors>
        <mat-error *ngIf="this.eventCloseColorForm.controls['closeColorInputCtrl'].hasError('invalidColor')">
          The color is invalid.
        </mat-error>
      </mat-form-field>
    </form>

    <mat-form-field class="full-width-container">
      <mat-label for="eventCloseTime">Close event X time before event start</mat-label>
      <input matInput class="form-control" id="eventCloseTime" placeholder="1w 2d 3h 10m"
        [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
        [value]="channelInfo.event_close_time | duration" (change)="setEventCloseTime($event.target)"
      />
      <mat-hint align="end">Combination of w(eeks), d(ays), h(ours), m(inutes). Example: 1d 5h</mat-hint>
    </mat-form-field>

    <mat-form-field class="full-width-container">
      <mat-label>Close Behavior</mat-label>
      <mat-select
        id="closeBehavior"
        [(ngModel)]="channelInfo.close_behavior"
        name="closeBehavior"
        [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
        (selectionChange)="setEventCloseBehavior($event)">
        <mat-option value="event start">Close relative to event start (default)</mat-option>
        <mat-option value="event end">        Close relative to event end</mat-option>
      </mat-select>
    </mat-form-field>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Export Event Settings</h2>
        <mat-icon class="section-icon">folder_shared</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/export/export-events" target="_blank" rel="noopener">Docs</a>
      </div>
    <div class="toggle-grid">
      <div class="toggle-item toggleExportEventOnClose">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.export_event_on_close"
                          (change)="toggleExportEventOnClose()">
          Export event to export channel on close
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/export_event toggle</small>
        </mat-slide-toggle>
      </div>
    </div>
    <form class="exportChannelSelection">
      <mat-form-field class="full-width-container" floatLabel="always">
        <mat-label for="exportChannelSelection">Export channel</mat-label>
        <mat-select
                [(ngModel)]="channelInfo.export_channel_id"
                name="exportChannel"
                id="exportChannelSelection"
                [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                (closed)="clearChannelFilter(exportChannelFilter)"
                [compareWith]="sameChannelId"
                (selectionChange)="setExportChannel($event)">
          <mat-option>
            <ngx-mat-select-search
              [formControl]="exportChannelFilter"
              placeholderLabel="Search channels"
              noEntriesFoundLabel="No channels found">
            </ngx-mat-select-search>
          </mat-option>
          <mat-option *ngFor="let channel of filteredExportChannels" [value]="channel.id" [selected]="sameChannelId(channel.id, channelInfo.export_channel_id)">
            {{ channel.name }}
          </mat-option>
        </mat-select>
        <mat-hint align="end">Channel to which events are exported</mat-hint>
      </mat-form-field>
    </form>
    <mat-label for="exportEventFile">Add event info file to exported events</mat-label>
    <div class="row pe-3">
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>Export file type</mat-label>
          <mat-select id="exportFile1" [(ngModel)]="exportFile" name="exportFile" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="0">Do not attach a file to the exported event</mat-option>
            <mat-option value="1">Attach a .csv file</mat-option>
            <mat-option value="2">Attach a .json file</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>User config</mat-label>
          <mat-select id="exportSignupState" [(ngModel)]="exportSignupState" name="exportSignupState" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="0">Don't export users</mat-option>
            <mat-option value="1">Only export signed up users</mat-option>
            <mat-option value="2">Only export signed off users</mat-option>
            <mat-option value="3">Export all users</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-2 pe-0 d-flex align-items-end">
        <button mat-button class="confirm-button" mat-raised-button color="primary" [disabled]="(exportFile === exportFileCurrent && exportSignupState === exportSignupStateCurrent)" (click)="setExportFileConfig()">Confirm</button>
      </div>
    </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Confirm Settings</h2>
        <mat-icon class="section-icon">check_circle</mat-icon>
      </div>
    <div class="toggle-grid">
      <div class="toggle-item toggleUserConfirmationMessage">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.msg_confirmed_usr"
                          (change)="toggleUserConfirmationMessage()">
          Users receive a confirmation message on confirmation
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle user_confirmation_message</small>
        </mat-slide-toggle>
      </div>
      <div class="toggle-item declineMessageSwitch">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.get_msg_unconfirmed_usr"
                          (change)="toggleOrganizerUnconfirmedMessages()">
          Organizers receive a signed off message when a confirmed user signs off
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle organizer_unconfirmed_messages</small>
        </mat-slide-toggle>
      </div>
    </div>
    <mat-form-field class="full-width-container" floatLabel="always">
      <mat-label>Confirm behavior</mat-label>
      <mat-select [(ngModel)]="channelInfo.confirmed_behavior" name="ConfirmBehavior" [disabled]="!(guild.owner || channelInfo.has_organizer_role)" (selectionChange)="setConfirmBehavior($event.value)">
        <mat-option [value]="1" [selected]="'1' === channelInfo.confirmed_behavior">
          Show confirm emoji next to user
        </mat-option>
        <mat-option [value]="2" [selected]="'2' === channelInfo.confirmed_behavior">
          Display confirmed users in separate field
        </mat-option>
        <mat-option [value]="3" [selected]="'3' === channelInfo.confirmed_behavior">
          Both
        </mat-option>
      </mat-select>
    </mat-form-field>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Thread Settings</h2>
        <mat-icon class="section-icon">forum</mat-icon>
      </div>
    <div class="toggle-grid">
      <div class="toggle-item threadToggle">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.use_threads"
                          (change)="toggleThreads()">
          Attach threads to events
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted" *ngIf="!threadStatus">/thread enable</small>
          <small class="form-text text-muted" *ngIf="threadStatus">/thread disable</small>
        </mat-slide-toggle>
      </div>
      <div class="toggle-item threadAutodeleteToggle">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.thread_auto_del"
                          (change)="toggleThreadAutodelete()">
          Automatically delete threads when event is deleted
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/thread autodelete</small>
        </mat-slide-toggle>
      </div>
    </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section" id="event-create-notification">
      <div class="section-header">
        <h2 class="section-title">Event Create Notification</h2>
        <mat-icon fontSet="material-symbols-outlined" class="section-icon">calendar_add_on</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/notification/event-creation-notification" target="_blank" rel="noopener">Docs</a>
      </div>
      <div class="config-status config-status-unset" *ngIf="!channelInfo.event_creation">
        <mat-icon>info</mat-icon>
        <span>Not configured yet.</span>
      </div>
      <mat-form-field class="full-width-container">
        <mat-label>Target channel</mat-label>
        <mat-select
          [(ngModel)]="eventCreateNotifyChannelId"
          name="eventCreateNotifyChannelId"
          (closed)="clearChannelFilter(createChannelFilter)"
          [compareWith]="sameChannelId"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
          <mat-option>
            <ngx-mat-select-search
              [formControl]="createChannelFilter"
              placeholderLabel="Search channels"
              noEntriesFoundLabel="No channels found">
            </ngx-mat-select-search>
          </mat-option>
          <mat-option value="">Event channel</mat-option>
          <mat-option *ngFor="let channel of filteredCreateChannels" [value]="channel.id">
            {{ channel.name }}
          </mat-option>
        </mat-select>
        <mat-hint>Posts in this channel. Event channel keeps the message next to the event.</mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Notification text</mat-label>
        <textarea
          matInput
          rows="2"
          [(ngModel)]="eventCreateNotifyMessage"
          name="eventCreateNotifyMessage"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"></textarea>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Embed text</mat-label>
        <textarea
          matInput
          rows="2"
          [(ngModel)]="eventCreateNotifyEmbed"
          name="eventCreateNotifyEmbed"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"></textarea>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Auto-delete</mat-label>
        <mat-select
          [(ngModel)]="eventCreateDeleteMode"
          name="eventCreateDeleteMode"
          (ngModelChange)="onEventCreateDeleteChanged()"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
          <mat-option value="never">Never</mat-option>
          <mat-option value="after">After a delay</mat-option>
          <mat-option value="event_start">When the event starts</mat-option>
          <mat-option value="event_deletion">When the event is deleted</mat-option>
        </mat-select>
        <mat-hint>Applies only to this channel's create notification.</mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container" *ngIf="eventCreateDeleteMode === 'after'">
        <mat-label>Delete after</mat-label>
        <input
          matInput
          [(ngModel)]="eventCreateDeleteAfter"
          (ngModelChange)="onEventCreateDeleteChanged()"
          name="eventCreateDeleteAfter"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
          placeholder="1d 5h">
        <mat-error *ngIf="eventCreateDeleteInvalid">Use a valid time like 1d 5h.</mat-error>
        <mat-hint align="end">Combination of w(eeks), d(ays), h(ours), m(inutes). Example: 1d 5h</mat-hint>
      </mat-form-field>
      <div class="premium-note" *ngIf="!guild.has_premium">
        <mat-icon>workspace_premium</mat-icon>
        <span>Custom event-create messages require premium.</span>
        <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
      </div>
      <div class="action-buttons-container compact-actions">
        <div class="primary-actions">
          <button
            mat-raised-button
            color="primary"
            class="save-button"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || eventCreateDeleteInvalid"
            (click)="saveEventCreateNotification(channelInfo)">
            <mat-icon>save</mat-icon>
            <span>Save</span>
          </button>
        </div>
        <div class="secondary-actions">
          <span
            class="remove-tooltip"
            matTooltip="Nothing configured yet to remove."
            [matTooltipDisabled]="!!channelInfo.event_creation">
            <button
              mat-stroked-button
              color="warn"
              class="delete-button"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !channelInfo.event_creation"
              (click)="removeEventCreateNotification(channelInfo)">
              <mat-icon>delete</mat-icon>
              <span>Remove</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section" id="event-start-notification">
      <div class="section-header">
        <h2 class="section-title">Event Start Notifications</h2>
        <mat-icon class="section-icon">notifications_active</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/notification/event-start-notification" target="_blank" rel="noopener">Docs</a>
      </div>
      <div class="config-status config-status-unset" *ngIf="(channelInfo.notifications || []).length === 0">
        <mat-icon>info</mat-icon>
        <span>No start notifications configured yet.</span>
      </div>
      <p class="notification-section-hint">Direct messages stay. Event end needs an end time.</p>
      <ng-container *ngIf="canAddEventStartNotification(channelInfo)">
        <div class="notification-list-header">Add a notification</div>
        <mat-form-field class="full-width-container">
          <mat-label>Notify state</mat-label>
          <mat-select
            [(ngModel)]="eventStartNotifyState"
            name="eventStartNotifyState"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option *ngFor="let option of startNotifyStateOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>Notify type</mat-label>
          <mat-select
            [(ngModel)]="eventStartNotifyType"
            name="eventStartNotifyType"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="Channel">Channel</mat-option>
            <mat-option value="DM">DM</mat-option>
            <mat-option value="DM & Channel">DM & Channel</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="full-width-container" *ngIf="eventStartUsesChannel()">
          <mat-label>Target channel</mat-label>
          <mat-select
            [(ngModel)]="eventStartNotifyChannelId"
            name="eventStartNotifyChannelId"
            (closed)="clearChannelFilter(startChannelFilter)"
            [compareWith]="sameChannelId"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option>
              <ngx-mat-select-search
                [formControl]="startChannelFilter"
                placeholderLabel="Search channels"
                noEntriesFoundLabel="No channels found">
              </ngx-mat-select-search>
            </mat-option>
            <mat-option value="">Event channel</mat-option>
            <mat-option *ngFor="let channel of filteredStartChannels" [value]="channel.id">
              {{ channel.name }}
            </mat-option>
          </mat-select>
          <mat-hint>Posts in this channel. Event channel keeps the message next to the event.</mat-hint>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>Time before event start</mat-label>
          <input
            matInput
            [(ngModel)]="eventStartNotifyTime"
            (ngModelChange)="onEventStartNotifyTimeChanged()"
            name="eventStartNotifyTime"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
            placeholder="15m">
          <mat-error *ngIf="eventStartNotifyTimeInvalid">Use a valid time like 15m or 1h 30m.</mat-error>
        </mat-form-field>
        <mat-form-field class="full-width-container">
          <mat-label>Auto-delete</mat-label>
          <mat-select
            [(ngModel)]="eventStartDeleteWhen"
            name="eventStartDeleteWhen"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option *ngFor="let option of startDeleteOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <div *ngIf="guild.has_premium">
          <mat-form-field class="full-width-container" *ngIf="eventStartUsesChannel()">
            <mat-label>Channel notification text (optional)</mat-label>
            <textarea
              matInput
              rows="2"
              [(ngModel)]="eventStartNotifyChannelText"
              name="eventStartNotifyChannelText"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
          </mat-form-field>
          <mat-form-field class="full-width-container" *ngIf="eventStartUsesChannel()">
            <mat-label>Channel embed text (optional)</mat-label>
            <textarea
              matInput
              rows="2"
              [(ngModel)]="eventStartNotifyChannelEmbed"
              name="eventStartNotifyChannelEmbed"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
          </mat-form-field>
          <mat-form-field class="full-width-container" *ngIf="eventStartUsesDm()">
            <mat-label>DM text (optional)</mat-label>
            <textarea
              matInput
              rows="2"
              [(ngModel)]="eventStartNotifyDmText"
              name="eventStartNotifyDmText"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
          </mat-form-field>
          <mat-form-field class="full-width-container" *ngIf="eventStartUsesDm()">
            <mat-label>DM embed text (optional)</mat-label>
            <textarea
              matInput
              rows="2"
              [(ngModel)]="eventStartNotifyDmEmbed"
              name="eventStartNotifyDmEmbed"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
          </mat-form-field>
        </div>
        <div class="premium-note" *ngIf="!guild.has_premium">
          <mat-icon>workspace_premium</mat-icon>
          <span>Custom start-notification messages require premium.</span>
          <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
        </div>
        <div class="action-buttons-container compact-actions">
          <div class="primary-actions">
            <button
              mat-raised-button
              color="primary"
              class="save-button"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role) || eventStartNotifyTimeInvalid || eventStartAdding"
              (click)="addEventStartNotification(channelInfo)">
              <mat-icon>add</mat-icon>
              <span>Add Notification</span>
            </button>
          </div>
        </div>
      </ng-container>
      <div class="premium-note" *ngIf="eventStartAddLimitReached(channelInfo)">
        <mat-icon>workspace_premium</mat-icon>
        <span>Another start notification needs premium.</span>
        <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
      </div>
      <div class="mt-3" *ngIf="(channelInfo.notifications || []).length > 0">
        <div class="notification-list-header">Configured notifications</div>
        <div class="notification-list">
          <div
            *ngFor="let notification of channelInfo.notifications"
            class="notification-row"
            [class.notification-row-editing]="isEditingEventStartNotification(notification)">
            <ng-container *ngIf="!isEditingEventStartNotification(notification)">
              <div class="notification-summary">
                <div class="notification-cell">
                  <span class="notification-label">Type</span>
                  <span class="notification-value">{{ notification.type }}</span>
                </div>
                <div class="notification-cell">
                  <span class="notification-label">State</span>
                  <span class="notification-value">{{ formatNotificationState(notification.state) }}</span>
                </div>
                <div class="notification-cell">
                  <span class="notification-label">Time before start</span>
                  <span class="notification-value">{{ notification.seconds | duration }}</span>
                </div>
                <div class="notification-cell">
                  <span class="notification-label">Auto-delete</span>
                  <span class="notification-value">{{ formatStartDeleteWhen(notification.delete_when) }}</span>
                </div>
                <div class="notification-actions">
                  <button
                    mat-stroked-button
                    class="save-button"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !canStartAnotherEventStartEdit(notification)"
                    (click)="beginEditEventStartNotification(notification)">
                    <mat-icon>edit</mat-icon>
                    <span>Edit</span>
                  </button>
                  <button
                    mat-stroked-button
                    color="warn"
                    class="delete-button"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                    (click)="removeEventStartNotification(notification.identifier)">
                    <mat-icon>delete</mat-icon>
                    <span>Remove</span>
                  </button>
                </div>
              </div>
              <div class="notification-target" *ngIf="notificationUsesChannel(notification.type)">
                <span class="notification-label">Target channel</span>
                <span class="notification-value">{{ channelName(notification.channel_id, channelInfo.channels) }}</span>
              </div>
              <div class="notification-message-preview" *ngIf="notificationUsesChannel(notification.type)">
                <div class="notification-message-grid">
                  <div class="notification-message-item">
                    <span class="notification-label">Channel text</span>
                    <span class="notification-value">{{ getStartNotificationTextPreview(notification, 'c_c') }}</span>
                  </div>
                  <div class="notification-message-item">
                    <span class="notification-label">Channel embed</span>
                    <span class="notification-value">{{ getStartNotificationTextPreview(notification, 'c_ec', 180) }}</span>
                  </div>
                </div>
              </div>
              <div class="notification-message-preview" *ngIf="notificationUsesDm(notification.type)">
                <div class="notification-message-grid">
                  <div class="notification-message-item">
                    <span class="notification-label">DM text</span>
                    <span class="notification-value">{{ getStartNotificationTextPreview(notification, 'dm_c') }}</span>
                  </div>
                  <div class="notification-message-item">
                    <span class="notification-label">DM embed</span>
                    <span class="notification-value">{{ getStartNotificationTextPreview(notification, 'dm_ec', 180) }}</span>
                  </div>
                </div>
              </div>
            </ng-container>
            <div class="notification-edit" *ngIf="editingEventStartDraft(notification) as draft">
              <div class="notification-list-header">Edit notification</div>
              <mat-form-field class="full-width-container">
                <mat-label>Notify state</mat-label>
                <mat-select
                  [(ngModel)]="draft.state"
                  [name]="'editStartState' + notification.identifier"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
                  <mat-option *ngFor="let option of startNotifyStateOptions" [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="full-width-container">
                <mat-label>Notify type</mat-label>
                <mat-select
                  [(ngModel)]="draft.type"
                  [name]="'editStartType' + notification.identifier"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
                  <mat-option value="Channel">Channel</mat-option>
                  <mat-option value="DM">DM</mat-option>
                  <mat-option value="DM & Channel">DM & Channel</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="full-width-container">
                <mat-label>Time before event start</mat-label>
                <input
                  matInput
                  [(ngModel)]="draft.time"
                  (ngModelChange)="onEventStartEditTimeChanged()"
                  [name]="'editStartTime' + notification.identifier"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                  placeholder="15m">
                <mat-error *ngIf="draft.timeInvalid">Use a valid time like 15m or 1h 30m.</mat-error>
              </mat-form-field>
              <mat-form-field class="full-width-container">
                <mat-label>Auto-delete</mat-label>
                <mat-select
                  [(ngModel)]="draft.deleteWhen"
                  [name]="'editStartDelete' + notification.identifier"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
                  <mat-option *ngFor="let option of startDeleteOptions" [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field class="full-width-container" *ngIf="notificationUsesChannel(draft.type)">
                <mat-label>Target channel</mat-label>
                <mat-select
                  [(ngModel)]="draft.channelId"
                  [name]="'editStartChannel' + notification.identifier"
                  (closed)="clearChannelFilter(startEditChannelFilter)"
                  [compareWith]="sameChannelId"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
                  <mat-option>
                    <ngx-mat-select-search
                      [formControl]="startEditChannelFilter"
                      placeholderLabel="Search channels"
                      noEntriesFoundLabel="No channels found">
                    </ngx-mat-select-search>
                  </mat-option>
                  <mat-option value="">Event channel</mat-option>
                  <mat-option *ngFor="let channel of filteredStartEditChannels" [value]="channel.id">
                    {{ channel.name }}
                  </mat-option>
                </mat-select>
                <mat-hint>Posts in this channel. Event channel keeps the message next to the event.</mat-hint>
              </mat-form-field>
              <div *ngIf="guild.has_premium">
                <mat-form-field class="full-width-container" *ngIf="notificationUsesChannel(draft.type)">
                  <mat-label>Channel notification text (optional)</mat-label>
                  <textarea
                    matInput
                    rows="2"
                    [(ngModel)]="draft.channelText"
                    [name]="'editStartChannelText' + notification.identifier"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
                </mat-form-field>
                <mat-form-field class="full-width-container" *ngIf="notificationUsesChannel(draft.type)">
                  <mat-label>Channel embed text (optional)</mat-label>
                  <textarea
                    matInput
                    rows="2"
                    [(ngModel)]="draft.channelEmbed"
                    [name]="'editStartChannelEmbed' + notification.identifier"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
                </mat-form-field>
                <mat-form-field class="full-width-container" *ngIf="notificationUsesDm(draft.type)">
                  <mat-label>DM text (optional)</mat-label>
                  <textarea
                    matInput
                    rows="2"
                    [(ngModel)]="draft.dmText"
                    [name]="'editStartDmText' + notification.identifier"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
                </mat-form-field>
                <mat-form-field class="full-width-container" *ngIf="notificationUsesDm(draft.type)">
                  <mat-label>DM embed text (optional)</mat-label>
                  <textarea
                    matInput
                    rows="2"
                    [(ngModel)]="draft.dmEmbed"
                    [name]="'editStartDmEmbed' + notification.identifier"
                    [disabled]="!(guild.owner || channelInfo.has_organizer_role)"></textarea>
                </mat-form-field>
              </div>
              <div class="notification-actions">
                <button
                  mat-raised-button
                  color="primary"
                  class="save-button"
                  [disabled]="!(guild.owner || channelInfo.has_organizer_role) || draft.timeInvalid || eventStartSaving"
                  (click)="saveEventStartNotification(channelInfo)">
                  <mat-icon>save</mat-icon>
                  <span>Save</span>
                </button>
                <button
                  mat-stroked-button
                  class="save-button"
                  [disabled]="eventStartSaving"
                  (click)="cancelEditEventStartNotification()">
                  <span>Cancel</span>
                </button>
                <button
                  mat-stroked-button
                  color="warn"
                  class="delete-button"
                  disabled>
                  <mat-icon>delete</mat-icon>
                  <span>Remove</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section" id="event-cancel-notification">
      <div class="section-header">
        <h2 class="section-title">Event Cancel Notification</h2>
        <mat-icon class="section-icon">event_busy</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/notification/event-cancel-notification" target="_blank" rel="noopener">Docs</a>
      </div>
      <div class="config-status config-status-unset" *ngIf="!channelInfo.cancel_notification">
        <mat-icon>info</mat-icon>
        <span>Not configured yet.</span>
      </div>
      <div class="premium-note" *ngIf="!guild.has_premium">
        <mat-icon>workspace_premium</mat-icon>
        <span>Event cancel notifications are a premium feature.</span>
        <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
      </div>
      <mat-form-field class="full-width-container">
        <mat-label>Notify type</mat-label>
        <mat-select
          [(ngModel)]="eventCancelNotifyType"
          name="eventCancelNotifyType"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
          <mat-option value="Channel">Channel</mat-option>
          <mat-option value="DM">DM</mat-option>
          <mat-option value="DM & Channel">DM & Channel</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width-container" *ngIf="eventCancelUsesChannel()">
        <mat-label>Target channel</mat-label>
        <mat-select
          [(ngModel)]="eventCancelNotifyChannelId"
          name="eventCancelNotifyChannelId"
          (closed)="clearChannelFilter(cancelChannelFilter)"
          [compareWith]="sameChannelId"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
          <mat-option>
            <ngx-mat-select-search
              [formControl]="cancelChannelFilter"
              placeholderLabel="Search channels"
              noEntriesFoundLabel="No channels found">
            </ngx-mat-select-search>
          </mat-option>
          <mat-option value="">Event channel</mat-option>
          <mat-option *ngFor="let channel of filteredCancelChannels" [value]="channel.id">
            {{ channel.name }}
          </mat-option>
        </mat-select>
        <mat-hint>Posts in this channel. Event channel keeps the message next to the event.</mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Time before event start</mat-label>
        <input
          matInput
          [(ngModel)]="eventCancelNotifyTime"
          (ngModelChange)="onEventCancelNotifyTimeChanged()"
          name="eventCancelNotifyTime"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
          placeholder="15m">
        <mat-error *ngIf="eventCancelNotifyTimeInvalid">Use a valid time like 15m or 1h 30m.</mat-error>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Minimum signups required</mat-label>
        <input
          matInput
          type="number"
          min="0"
          [(ngModel)]="eventCancelNotifySignups"
          name="eventCancelNotifySignups"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Auto-delete</mat-label>
        <mat-select
          [(ngModel)]="eventCancelDeleteMode"
          name="eventCancelDeleteMode"
          (ngModelChange)="onEventCancelDeleteChanged()"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
          <mat-option value="never">Never</mat-option>
          <mat-option value="after">After a delay</mat-option>
        </mat-select>
        <mat-hint>Applies only to this channel's cancel notification. Direct messages stay.</mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container" *ngIf="eventCancelDeleteMode === 'after'">
        <mat-label>Delete after</mat-label>
        <input
          matInput
          [(ngModel)]="eventCancelDeleteAfter"
          (ngModelChange)="onEventCancelDeleteChanged()"
          name="eventCancelDeleteAfter"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
          placeholder="1d 5h">
        <mat-error *ngIf="eventCancelDeleteInvalid">Use a valid time like 1d 5h.</mat-error>
        <mat-hint align="end">Combination of w(eeks), d(ays), h(ours), m(inutes). Example: 1d 5h</mat-hint>
      </mat-form-field>
      <div class="action-buttons-container compact-actions">
        <div class="primary-actions">
          <button
            mat-raised-button
            color="primary"
            class="save-button"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium || eventCancelNotifyTimeInvalid || eventCancelDeleteInvalid"
            (click)="saveEventCancelNotification(channelInfo)">
            <mat-icon>save</mat-icon>
            <span>Save</span>
          </button>
        </div>
        <div class="secondary-actions">
          <span
            class="remove-tooltip"
            matTooltip="Nothing configured yet to remove."
            [matTooltipDisabled]="!!channelInfo.cancel_notification">
            <button
              mat-stroked-button
              color="warn"
              class="delete-button"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium || !channelInfo.cancel_notification"
              (click)="removeEventCancelNotification(channelInfo)">
              <mat-icon>delete</mat-icon>
              <span>Remove</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Logging Setup</h2>
        <mat-icon class="section-icon">receipt_long</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/logging" target="_blank" rel="noopener">Docs</a>
      </div>
      <div class="config-status config-status-unset" *ngIf="!channelInfo.log">
        <mat-icon>info</mat-icon>
        <span>Not configured yet.</span>
      </div>
      <div class="premium-note" *ngIf="!guild.has_premium">
        <mat-icon>workspace_premium</mat-icon>
        <span>Logging setup is a premium feature.</span>
        <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
      </div>
      <mat-form-field class="full-width-container">
        <mat-label>Log destination channel</mat-label>
        <mat-select
          [(ngModel)]="loggingChannelId"
          name="loggingChannelId"
          (closed)="clearChannelFilter(logChannelFilter)"
          [compareWith]="sameChannelId"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
          <mat-option>
            <ngx-mat-select-search
              [formControl]="logChannelFilter"
              placeholderLabel="Search channels"
              noEntriesFoundLabel="No channels found">
            </ngx-mat-select-search>
          </mat-option>
          <mat-option *ngFor="let channel of filteredLogChannels" [value]="channel.id">
            {{ channel.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <div class="logging-toggle-grid">
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logEventCreation" name="logEventCreation" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">Event creation</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logEventEdit" name="logEventEdit" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">Event edit</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logEventClosed" name="logEventClosed" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">Event closed</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logEventOpened" name="logEventOpened" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">Event opened</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logEventDeleted" name="logEventDeleted" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">Event deleted</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logUserSignUp" name="logUserSignUp" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">User sign up</mat-slide-toggle></div>
        <div class="logging-toggle-item"><mat-slide-toggle [(ngModel)]="logUserSignOff" name="logUserSignOff" [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">User sign off</mat-slide-toggle></div>
      </div>
      <div class="action-buttons-container compact-actions">
        <div class="primary-actions">
          <button
            mat-raised-button
            color="primary"
            class="save-button"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
            (click)="saveLoggingSetup(channelInfo)">
            <mat-icon>save</mat-icon>
            <span>Save</span>
          </button>
        </div>
        <div class="secondary-actions">
          <span
            class="remove-tooltip"
            matTooltip="Nothing configured yet to remove."
            [matTooltipDisabled]="!!channelInfo.log">
            <button
              mat-stroked-button
              color="warn"
              class="delete-button"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium || !channelInfo.log"
              (click)="removeLoggingSetup(channelInfo)">
              <mat-icon>delete</mat-icon>
              <span>Remove</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Voice Channel Settings</h2>
        <mat-icon class="section-icon">graphic_eq</mat-icon>
        <a class="docs-link-inline ms-auto" href="https://docs.raidorganizer.org/voice-channel" target="_blank" rel="noopener">Docs</a>
      </div>
      <div class="premium-note" *ngIf="!guild.has_premium">
        <mat-icon>workspace_premium</mat-icon>
        <span>Voice channel automation is a premium feature.</span>
        <a [routerLink]="['/guilds', guildId, 'premium']">Get Premium</a>
      </div>
      <mat-form-field class="full-width-container">
        <mat-label>Voice channel name template</mat-label>
        <input
          matInput
          [(ngModel)]="voiceName"
          name="voiceName"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
          placeholder="event title template">
        <mat-hint align="end">Supports <a href="https://docs.raidorganizer.org/voice-channel" target="_blank">dynamic fields</a></mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Create voice channel before event start</mat-label>
        <input
          matInput
          [(ngModel)]="voiceTime"
          (ngModelChange)="onVoiceTimeChanged()"
          name="voiceTime"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
          placeholder="5m">
        <mat-error *ngIf="voiceTimeInvalid">Use a valid time like 5m, 1h 30m, or 1d 2h.</mat-error>
        <mat-hint align="end">Use w, d, h, m (example: 1h 30m)</mat-hint>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>Voice category</mat-label>
        <mat-select
          [(ngModel)]="voiceCategoryId"
          name="voiceCategoryId"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium">
          <mat-option *ngFor="let category of channelInfo.categories" [value]="category.id">
            {{ category.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field class="full-width-container">
        <mat-label>User limit</mat-label>
        <input
          matInput
          type="number"
          min="1"
          max="99"
          [(ngModel)]="voiceLimit"
          name="voiceLimit"
          [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
          placeholder="No limit">
        <mat-hint align="end">Leave empty for no limit</mat-hint>
      </mat-form-field>
      <div class="toggle-grid">
        <div class="toggle-item toggleVoiceViewRestriction">
          <mat-slide-toggle
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
            [(ngModel)]="voiceViewRestriction"
            name="voiceViewRestriction">
            Only event role can view the voice channel
          </mat-slide-toggle>
        </div>
        <div class="toggle-item toggleVoiceConnectRestriction">
          <mat-slide-toggle
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
            [(ngModel)]="voiceConnectRestriction"
            name="voiceConnectRestriction">
            Only event role can connect to the voice channel
          </mat-slide-toggle>
        </div>
      </div>
      <div class="action-buttons-container compact-actions">
        <div class="primary-actions">
          <button
            mat-raised-button
            color="primary"
            class="save-button"
            [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium || voiceTimeInvalid"
            (click)="saveVoiceSettings(channelInfo)">
            <mat-icon>save</mat-icon>
            <span>Save Voice Settings</span>
          </button>
        </div>
        <div class="secondary-actions">
          <span
            class="remove-tooltip"
            matTooltip="Nothing configured yet to remove."
            [matTooltipDisabled]="!!channelInfo.voice">
            <button
              mat-stroked-button
              color="warn"
              class="delete-button"
              [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium || !channelInfo.voice"
              (click)="removeVoiceSettings(channelInfo)">
              <mat-icon>delete</mat-icon>
              <span>Remove Voice Settings</span>
            </button>
          </span>
        </div>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section">
      <div class="section-header">
        <h2 class="section-title">Miscellaneous</h2>
        <mat-icon class="section-icon">settings</mat-icon>
      </div>
    <mat-form-field class="full-width-container" floatLabel="always">
      <mat-label>Display User Names</mat-label>
      <mat-select [(ngModel)]="channelInfo.display_names" name="DisplayNames" [disabled]="!(guild.owner || channelInfo.has_organizer_role)" (selectionChange)="setDisplayNames($event.value)">
        <mat-option [value]="1" [selected]="'1' === channelInfo.display_names">
          Server Nickname
        </mat-option>
        <mat-option [value]="2" [selected]="'2' === channelInfo.display_names">
          User Display Name
        </mat-option>
        <mat-option [value]="3" [selected]="'3' === channelInfo.display_names">
          Account Username
        </mat-option>
        <mat-option [value]="4" [selected]="'4' === channelInfo.display_names">
          @mention
        </mat-option>
      </mat-select>
    </mat-form-field>

    <div class="toggle-grid">
      <div class="toggle-item toggleFooterTime">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.show_footer_time"
                          (change)="toggleFooterTime()">
          Show the event start time in the event footer as well
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle footer_time</small>
        </mat-slide-toggle>
      </div>

      <ng-container *ngIf="channelInfo.type !== 'event'">
        <div class="toggle-item toggleAtHere">
          <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                            [checked]="channelInfo.at_here_notification"
                            (change)="toggleHereNotification()">
              Send @here notification on event creation
            <!-- TODO: Doc and cooldown -->
            <small class="form-text text-muted">/toggle here_notification</small>
          </mat-slide-toggle>
        </div>
      </ng-container>
      <div class="toggle-item toggleScheduledEvents">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.create_scheduled"
                          (change)="toggleScheduledEvents()">
            Create Discord/Scheduled events on event creation
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle discord_events</small>
        </mat-slide-toggle>
      </div>
      <div class="toggle-item toggleEventRoles">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.create_event_role ?? false"
                          (change)="toggleEventRoles()">
            Create temporary event roles and sync with signups
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle event_roles</small>
        </mat-slide-toggle>
      </div>
      <div class="toggle-item toggleEventCreateOverlapNote">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role) || !guild.has_premium"
                          [checked]="channelInfo.overlap_note"
                          (change)="toggleEventCreateOverlapNote()">
            Get a notification message if you create an event that overlaps with another event
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle event_create_overlap_note</small>
        </mat-slide-toggle>
      </div>
      <div class="toggle-item toggleShowUserChoices">
        <mat-slide-toggle [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
                          [checked]="channelInfo.user_choices"
                          (change)="toggleShowUserChoices()">
          On multiple sign ups, show preferred / other choices next to user
          <!-- TODO: Doc and cooldown -->
          <small class="form-text text-muted">/toggle show_user_choices</small>
        </mat-slide-toggle>
      </div>
    </div>

    <mat-form-field class="full-width-container">
      <mat-label for="signupCooldown">Signup Cooldown (in seconds)</mat-label>
      <input matInput class="form-control" id="signupCooldownInput" placeholder="Signup cooldown time"
        [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
        [value]="+channelInfo.sign_up_cooldown" (change)="setSignupCooldown($event.target)"
        min="0" />
      <mat-hint align="end">Set to 0 to disable</mat-hint>
    </mat-form-field>

    <mat-form-field class="full-width-container">
      <mat-label for="eventDeletionInput">Event deletion time (in days)</mat-label>
      <input matInput class="form-control" id="eventDeletionInput" placeholder="Event deletion time"
        [disabled]="!(guild.owner || channelInfo.has_organizer_role)"
        [value]="+channelInfo.event_deletion_time" (input)="onEventDeletionTimeInput($event.target)" (change)="setEventDeletionTime($event.target)"
        min="0" [max]="(guild.has_premium) ? 60 : 14" />
      <mat-hint align="end">Max: {{(guild.has_premium) ? 60 : 14}}</mat-hint>
    </mat-form-field>

    <form class="organizerRoleSelection">
      <mat-form-field class="full-width-container" floatLabel="always">
        <mat-label for="templateSelection">Default Signup Template</mat-label>
        <mat-select [(ngModel)]="channelInfo.default_template" id="templateSelection" name="templateSelection" (selectionChange)="setDefaultTemplate($event)"
                [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
          <mat-optgroup label="Default templates">
            <mat-option *ngFor="let template of channelInfo.default_signup_templates; let i = index" [value]="i"
                    [selected]="i === +channelInfo.default_template">
              {{ template.name }}
            </mat-option>
          </mat-optgroup>
          <mat-optgroup label="Custom templates">
            <mat-option *ngFor="let template of channelInfo.custom_signup_templates; let i = index" [value]="i + 3"
                    [selected]="i === +channelInfo.default_template - 3">
              {{ template.name }}
            </mat-option>
          </mat-optgroup>
        </mat-select>
      </mat-form-field>
    </form>

    <mat-label for="CleanMode">Clean mode</mat-label>
    <div class="row pe-3">
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>Organizer behavior</mat-label>
          <mat-select id="organizerDeleteBehavior" [(ngModel)]="organizerDeleteBehavior" name="organizerDeleteBehavior" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="0">Delete all messages from organizers</mat-option>
            <mat-option value="1">Keep messages from organizers</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-5 pe-0">
        <mat-form-field class="full-width-container">
          <mat-label>User behavior</mat-label>
          <mat-select id="userDeleteBehavior" [(ngModel)]="userDeleteBehavior" name="userDeleteBehavior" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
            <mat-option value="0">Delete all messages from users</mat-option>
            <mat-option value="1">Keep messages from users with the user role</mat-option>
            <mat-option value="2">Keep messages from all users. Also from ones without the user role</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="col-12 col-md-2 pe-0 d-flex align-items-end">
        <button mat-button class="confirm-button" mat-raised-button color="primary" [disabled]="!(organizerDeleteBehavior && userDeleteBehavior) || (organizerDeleteBehavior === organizerDeleteBehaviorCurrent && userDeleteBehavior === userDeleteBehaviorCurrent)" (click)="setCleanMode()">Confirm</button>
      </div>
    </div>
    </div>
  </ng-container>

  <ng-container *ngIf="guild && channelInfo$ | async as channelInfo">
    <div class="settings-section" *ngIf="channelInfo.repeating_events && channelInfo.repeating_events.length > 0">
      <div class="section-header">
        <h2 class="section-title">Repeating Events List</h2>
        <mat-icon class="section-icon">repeat</mat-icon>
      </div>
      <table mat-table [dataSource]="channelInfo.repeating_events" class="mat-elevation-z8">
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef> Event Title </th>
          <td mat-cell *matCellDef="let event"> {{event.name}} </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef> Delete </th>
          <td mat-cell *matCellDef="let event">
            <button mat-icon-button (click)="removeRepeatingEvent(event.id)" [disabled]="!(guild.owner || channelInfo.has_organizer_role)">
              <fa-icon [icon]="faIcons.trash"></fa-icon>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="['name', 'actions']"></tr>
        <tr mat-row *matRowDef="let row; columns: ['name', 'actions'];"></tr>
      </table>
    </div>
  </ng-container>
  </div>

<ng-template #deleteChannelDialog>
  <h1 mat-dialog-title>Warning</h1>
  <div mat-dialog-content>
    <p>You are about to remove this channel setup.</p>
    <p>
      Removing <code>{{ channelToDeleteLabel }}</code> will stop Raid Organizer from listening in that channel and will
      remove related events from the bot configuration.
    </p>
    <div *ngIf="eventsImpacted.length > 0">
      <h2>Events</h2>
      <ul>
        <li *ngFor="let event of eventsImpacted">
          <a [href]="getEventUrl(event.id)" target="_blank" rel="noopener">
            {{ event.name }}
          </a>
        </li>
      </ul>
    </div>
    <div *ngIf="repeatingEventsImpacted.length > 0">
      <h2>Repeating Events</h2>
      <ul>
        <li *ngFor="let event of repeatingEventsImpacted">
          <code>{{ event.id }}</code> {{ event.name }}
        </li>
      </ul>
    </div>
    <p *ngIf="eventsImpacted.length === 0 && repeatingEventsImpacted.length === 0">
      <strong>No events found.</strong>
    </p>
  </div>
  <div mat-dialog-actions class="dialog-actions">
    <button mat-button [mat-dialog-close]="false">Cancel</button>
    <button mat-button color="warn" [mat-dialog-close]="true">Confirm</button>
  </div>
</ng-template>
